import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Bot, Loader2, RefreshCw, ThumbsDown, ThumbsUp, X } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { supabase } from "../../../lib/supabase";
import { SupportAiCostPanel } from "./SupportAiCostPanel";

/**
 * /admin/support-bot — usage du widget de support IA.
 * Source : RPC admin `admin_get_support_chat_stats` et
 * `admin_get_support_chat_conversation` (migration 20260916160000).
 */

interface KeyCount {
  key: string;
  count: number;
}

interface SupportChatStats {
  totals: {
    conversations: number;
    visitors: number;
    guest_conversations: number;
    account_conversations: number;
    user_messages: number;
    median_user_messages: number;
    ai_errors: number;
    ticket_proposed: number;
    ticket_created: number;
    roadmap_proposed: number;
    roadmap_clicked: number;
    self_served: number;
    rated_up: number;
    rated_down: number;
  };
  daily: Array<{
    day: string;
    conversations: number;
    visitors: number;
    tickets: number;
  }>;
  by_classification: KeyCount[];
  by_product: KeyCount[];
  by_locale: KeyCount[];
  by_page: KeyCount[];
  downvoted: Array<{
    conversation_id: string;
    rated_at: string;
    question: string | null;
    answer: string;
  }>;
  uncertain: Array<{
    conversation_id: string;
    created_at: string;
    question: string;
    classification: string | null;
    confidence: number | null;
  }>;
  recent: Array<{
    id: string;
    last_message_at: string;
    product: string | null;
    locale: string | null;
    is_guest: boolean;
    message_count: number;
    ticket_id: string | null;
    roadmap_clicked: boolean;
    first_question: string | null;
  }>;
}

interface ConversationDetail {
  conversation: { id: string; page: string | null; created_at: string };
  user_email: string | null;
  messages: Array<{
    id: string;
    role: "user" | "assistant";
    content: string;
    classification: string | null;
    confidence: number | null;
    is_error: boolean;
    rating: number | null;
    created_at: string;
  }>;
}

const RANGES = [7, 30, 90] as const;
type Range = (typeof RANGES)[number];

const pct = (part: number, total: number) =>
  total > 0 ? Math.round((part / total) * 100) : 0;

const StatTile: React.FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <div className="bg-card border border-border rounded-xl p-4 min-w-0">
    <div className="text-xs text-muted-foreground uppercase tracking-wide truncate">
      {label}
    </div>
    <div className="text-2xl font-bold text-foreground mt-1">{value}</div>
    {hint && (
      <div className="text-xs text-muted-foreground mt-1 truncate">{hint}</div>
    )}
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="bg-card border border-border rounded-xl p-4 min-w-0">
    <h2 className="text-sm font-semibold text-foreground mb-3">{title}</h2>
    {children}
  </section>
);

export const SupportBotDashboard: React.FC = () => {
  const { t, i18n } = useTranslation("admin");
  const [range, setRange] = useState<Range>(30);
  const [stats, setStats] = useState<SupportChatStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const to = new Date();
    const from = new Date(to.getTime() - range * 24 * 60 * 60 * 1000);
    const { data, error: rpcError } = await supabase.rpc(
      "admin_get_support_chat_stats",
      { p_from: from.toISOString(), p_to: to.toISOString() },
    );
    if (rpcError) setError(rpcError.message);
    else setStats(data as SupportChatStats);
    setLoading(false);
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  const openConversation = async (id: string) => {
    setDetailLoading(true);
    setDetail(null);
    const { data, error: rpcError } = await supabase.rpc(
      "admin_get_support_chat_conversation",
      { p_conversation_id: id },
    );
    if (rpcError) setError(rpcError.message);
    else setDetail(data as ConversationDetail);
    setDetailLoading(false);
  };

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      dateStyle: "short",
      timeStyle: "short",
    });
  const labelFor = (group: string, key: string) =>
    t(`support_bot.${group}.${key}`, { defaultValue: key });

  const tot = stats?.totals;
  const rated = tot ? tot.rated_up + tot.rated_down : 0;
  const selfServed = tot?.self_served ?? 0;

  return (
    <div className="min-h-screen-safe bg-background text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Bot className="w-6 h-6 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl font-semibold truncate">
                {t("support_bot.title")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("support_bot.subtitle")}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                aria-pressed={range === r}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                  range === r
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card border-border hover:bg-muted"
                }`}
              >
                {t("support_bot.range_days", { count: r })}
              </button>
            ))}
            <button
              type="button"
              onClick={() => void load()}
              aria-label={t("support_bot.refresh")}
              title={t("support_bot.refresh")}
              className="p-2 rounded-md border border-border bg-card hover:bg-muted"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <Link
              to="/admin"
              className="text-sm text-muted-foreground hover:text-foreground px-2"
            >
              {t("support_bot.back_to_tickets")}
            </Link>
          </div>
        </header>

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("support_bot.loading")}
          </div>
        )}
        {error && (
          <div className="text-sm text-destructive">
            {t("support_bot.error", { message: error })}
          </div>
        )}

        <SupportAiCostPanel days={range} />

        {!loading && tot && tot.conversations === 0 && (
          <div className="text-muted-foreground">
            {t("support_bot.no_data")}
          </div>
        )}

        {!loading && stats && tot && tot.conversations > 0 && (
          <>
            {/* 1. Adoption */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile
                label={t("support_bot.kpi.conversations")}
                value={String(tot.conversations)}
                hint={t("support_bot.kpi.guest_split", {
                  guests: tot.guest_conversations,
                  accounts: tot.account_conversations,
                })}
              />
              <StatTile
                label={t("support_bot.kpi.visitors")}
                value={String(tot.visitors)}
              />
              <StatTile
                label={t("support_bot.kpi.messages")}
                value={String(tot.user_messages)}
                hint={t("support_bot.kpi.median", {
                  value: tot.median_user_messages,
                })}
              />
              <StatTile
                label={t("support_bot.kpi.self_served")}
                value={`${pct(selfServed, tot.conversations)} %`}
                hint={t("support_bot.kpi.self_served_hint")}
              />
              <StatTile
                label={t("support_bot.kpi.tickets")}
                value={String(tot.ticket_created)}
                hint={t("support_bot.kpi.of_proposed", {
                  count: tot.ticket_proposed,
                })}
              />
              <StatTile
                label={t("support_bot.kpi.roadmap")}
                value={String(tot.roadmap_clicked)}
                hint={t("support_bot.kpi.of_proposed", {
                  count: tot.roadmap_proposed,
                })}
              />
              <StatTile
                label={t("support_bot.kpi.satisfaction")}
                value={rated > 0 ? `${pct(tot.rated_up, rated)} %` : "—"}
                hint={t("support_bot.kpi.ratings", {
                  up: tot.rated_up,
                  down: tot.rated_down,
                })}
              />
              <StatTile
                label={t("support_bot.kpi.ai_errors")}
                value={String(tot.ai_errors)}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section title={t("support_bot.sections.daily")}>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.daily}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="day" fontSize={11} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="conversations"
                        name={t("support_bot.kpi.conversations")}
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="visitors"
                        name={t("support_bot.kpi.visitors")}
                        stroke="hsl(var(--muted-foreground))"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Section>

              {/* 2. Types de questions */}
              <Section title={t("support_bot.sections.classification")}>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={stats.by_classification.map((c) => ({
                        ...c,
                        label: labelFor("classification", c.key),
                      }))}
                      layout="vertical"
                      margin={{ left: 16 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis
                        type="number"
                        allowDecimals={false}
                        fontSize={11}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={110}
                        fontSize={11}
                      />
                      <Tooltip />
                      <Bar
                        dataKey="count"
                        name={t("support_bot.kpi.messages")}
                        fill="hsl(var(--primary))"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Section>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(
                [
                  ["by_product", stats.by_product],
                  ["by_locale", stats.by_locale],
                  ["by_page", stats.by_page],
                ] as const
              ).map(([key, rows]) => (
                <Section key={key} title={t(`support_bot.sections.${key}`)}>
                  <ul className="space-y-1 text-sm">
                    {rows.map((r) => (
                      <li key={r.key} className="flex justify-between gap-3">
                        <span className="truncate text-muted-foreground">
                          {r.key}
                        </span>
                        <span className="font-medium">
                          {r.count} · {pct(r.count, tot.conversations)} %
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              ))}
            </div>

            {/* 3. Qualité des réponses */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section title={t("support_bot.sections.downvoted")}>
                {stats.downvoted.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("support_bot.empty_list")}
                  </p>
                ) : (
                  <ul className="space-y-3 max-h-96 overflow-y-auto">
                    {stats.downvoted.map((d) => (
                      <li key={`${d.conversation_id}-${d.rated_at}`}>
                        <button
                          type="button"
                          onClick={() =>
                            void openConversation(d.conversation_id)
                          }
                          className="w-full text-left rounded-lg border border-border p-3 hover:bg-muted text-sm"
                        >
                          <div className="font-medium line-clamp-2 break-words">
                            {d.question ?? "—"}
                          </div>
                          <div className="text-muted-foreground line-clamp-3 break-words mt-1">
                            {d.answer}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {fmtDate(d.rated_at)}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title={t("support_bot.sections.uncertain")}>
                {stats.uncertain.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("support_bot.empty_list")}
                  </p>
                ) : (
                  <ul className="space-y-2 max-h-96 overflow-y-auto">
                    {stats.uncertain.map((u) => (
                      <li key={`${u.conversation_id}-${u.created_at}`}>
                        <button
                          type="button"
                          onClick={() =>
                            void openConversation(u.conversation_id)
                          }
                          className="w-full text-left rounded-lg border border-border p-3 hover:bg-muted text-sm"
                        >
                          <div className="line-clamp-2 break-words">
                            {u.question}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {labelFor(
                              "classification",
                              u.classification ?? "unclassified",
                            )}
                            {u.confidence != null &&
                              ` · ${Math.round(u.confidence * 100)} %`}
                            {` · ${fmtDate(u.created_at)}`}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>

            {/* 4. Conversations récentes */}
            <Section title={t("support_bot.sections.recent")}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="py-2 pr-3">
                        {t("support_bot.table.date")}
                      </th>
                      <th className="py-2 pr-3">
                        {t("support_bot.table.question")}
                      </th>
                      <th className="py-2 pr-3">
                        {t("support_bot.table.who")}
                      </th>
                      <th className="py-2 pr-3">
                        {t("support_bot.table.messages")}
                      </th>
                      <th className="py-2">{t("support_bot.table.outcome")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent.map((c) => (
                      <tr
                        key={c.id}
                        onClick={() => void openConversation(c.id)}
                        className="border-t border-border cursor-pointer hover:bg-muted"
                      >
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {fmtDate(c.last_message_at)}
                        </td>
                        <td className="py-2 pr-3 max-w-xs truncate">
                          {c.first_question ?? "—"}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {c.is_guest
                            ? t("support_bot.table.guest")
                            : t("support_bot.table.account")}
                          {c.product ? ` · ${c.product}` : ""}
                        </td>
                        <td className="py-2 pr-3">{c.message_count}</td>
                        <td className="py-2 whitespace-nowrap">
                          {c.ticket_id
                            ? t("support_bot.outcome.ticket")
                            : c.roadmap_clicked
                              ? t("support_bot.outcome.roadmap")
                              : t("support_bot.outcome.chat_only")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          </>
        )}
      </div>

      {(detail || detailLoading) && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex justify-end"
          onClick={() => setDetail(null)}
        >
          <aside
            className="w-full max-w-lg h-full bg-background border-l border-border flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t("support_bot.detail.title")}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="min-w-0">
                <h2 className="font-semibold">
                  {t("support_bot.detail.title")}
                </h2>
                {detail && (
                  <p className="text-xs text-muted-foreground truncate">
                    {detail.user_email ?? t("support_bot.table.guest")}
                    {detail.conversation.page
                      ? ` · ${detail.conversation.page}`
                      : ""}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setDetail(null);
                  setDetailLoading(false);
                }}
                aria-label={t("support_bot.detail.close")}
                className="p-2 rounded-md hover:bg-muted"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {detailLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {detail?.messages.map((m) => (
                <div
                  key={m.id}
                  className={`rounded-lg p-3 text-sm break-words whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground ml-8"
                      : "bg-muted mr-8"
                  }`}
                >
                  {m.content}
                  <div className="mt-1 text-xs opacity-75 flex items-center gap-2 flex-wrap">
                    {fmtDate(m.created_at)}
                    {m.classification &&
                      ` · ${labelFor("classification", m.classification)}`}
                    {m.confidence != null &&
                      ` · ${Math.round(m.confidence * 100)} %`}
                    {m.is_error && ` · ${t("support_bot.detail.error")}`}
                    {m.rating === 1 && <ThumbsUp className="w-3 h-3" />}
                    {m.rating === -1 && <ThumbsDown className="w-3 h-3" />}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default SupportBotDashboard;
